import React, { useState, useEffect, useCallback } from 'react'
import { useRouter } from '../lib/SmartRouter'
import './EnhancedRegistrationForm.css'
import './UnifiedDashboard.css'
import OrphanImageCleanup from './OrphanImageCleanup'
import { LocationPermissionsManager } from './LocationPermissionsManager'
import { ForceClearPanel } from './ForceClearPanel'
import { CardPrintingSection } from './CardPrintingSection'
import { UserPasswordManager } from './UserPasswordManager'



// Simple Error Boundary Component
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  constructor(props: {children: React.ReactNode}) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Certificate details error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '1rem', background: 'rgba(220, 53, 69, 0.1)', border: '1px solid rgba(220, 53, 69, 0.3)', borderRadius: '4px', color: '#dc3545' }}>
          <h4>⚠️ Error Loading Certificate Details</h4>
          <p>There was an error displaying the certificate details. Please try refreshing the page.</p>
          <button 
            onClick={() => this.setState({ hasError: false })}
            style={{ padding: '0.5rem 1rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

// Types (v12.6 - Complete 35-field interface)
interface Certificate {
  // Core certificate fields
  id: string
  cert_id: string
  user_id: string
  
  // Gemstone specifications
  gemstone_type: string
  gemstone_weight: number
  gemstone_color_range: string
  gemstone_clarity_range: string
  gemstone_growth_origin: 'NATURAL' | 'LAB GROWN' // NEW v12.6
  
  // Metal & physical properties
  metal_composition: string
  total_weight: number
  
  // Status & workflow
  status: 'DRAFT' | 'PUBLISHED'
  validation_status: 'VALID' | 'INVALID' | 'PENDING'
  
  // Design & media
  card_design_code: string // Now mandatory
  image_url?: string
  image_filename?: string
  image_size_bytes?: number
  
  // Timeline
  created_at: string
  updated_at: string
  published_at?: string
  
  // Comment lines (optional)
  comment_line_1?: string
  comment_line_2?: string
  comment_line_3?: string
  
  // Abbreviation lines (optional)
  abbreviation_line_1?: string
  abbreviation_line_2?: string
  abbreviation_line_3?: string
  abbreviation_line_4?: string
  abbreviation_line_5?: string
  abbreviation_line_6?: string
  abbreviation_line_7?: string
  abbreviation_line_8?: string
  abbreviation_line_9?: string
  abbreviation_line_10?: string
  abbreviation_line_11?: string
  abbreviation_line_12?: string
  abbreviation_line_13?: string
  
  // User info (joined from users table)
  created_by_name: string
  created_by_username: string
  user_location: string
}

interface SearchParams {
  query: string
  status?: string
  validation_status?: string
  date_from?: string
  date_to?: string
  created_by?: string
  gemstone_type?: string
  metal_composition?: string
  gemstone_growth_origin?: string // NEW v12.6
}

interface PaginationInfo {
  page: number
  limit: number
  total: number
  totalPages: number
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
}

interface PendingUser {
  id: string
  username: string
  email: string
  firstname: string
  lastname: string
  location: string
  mobilenumber: string
  role?: string
  rights?: string
  status?: string
  is_active?: boolean
  created_at: string
  last_login_at?: string
}

type DashboardModule = 'create' | 'admin' | 's3-upload' | 's3-presigned' | 's3-cleanup' | 'card-printing' | null

export const UnifiedDashboard: React.FC = () => {
  const { user, logout, navigate } = useRouter()
  
  // Navigation state - Start with no module selected
  const [activeModule, setActiveModule] = useState<DashboardModule>(null)
  
  // Certificate search state
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [searchParams, setSearchParams] = useState<SearchParams>({ query: '' })
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    sortBy: 'created_at',
    sortOrder: 'DESC'
  })
  const [selectedCertificates, setSelectedCertificates] = useState<string[]>([])
  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  
  // UI state
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isRefreshingSettings, setIsRefreshingSettings] = useState(false)
  const [settingsRefreshMessage, setSettingsRefreshMessage] = useState('')
  const [isRefreshingUsers, setIsRefreshingUsers] = useState(false)
  const [usersRefreshMessage, setUsersRefreshMessage] = useState('')
  const [userActionMessages, setUserActionMessages] = useState<{[key: string]: string}>({})
  
  // Admin state
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([])
  const [allUsers, setAllUsers] = useState<PendingUser[]>([])
  const [adminStats, setAdminStats] = useState<any>(null)
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    rejectedUsers: 0,
    adminUsers: 0,
    creatorUsers: 0,
    readOnlyUsers: 0,
    dubaiUsers: 0,
    dohaUsers: 0,
    recentRegistrations: 0
  })
  
  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState<{[key: string]: boolean}>({
    'basic': false,
    'gemstone': false,
    'physical': false,
    'creator': false,
    'technical': false,
    'media': false
  })
  const [systemSettings, setSystemSettings] = useState<any[]>([])
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(false)
  const [approvalWorkflowEnabled, setApprovalWorkflowEnabled] = useState(false)
  const [emailVerificationEnabled, setEmailVerificationEnabled] = useState(false)
  const [mobileVerificationEnabled, setMobileVerificationEnabled] = useState(false)
  const [verificationRequiredForLogin, setVerificationRequiredForLogin] = useState(false)

  // Country code mapping for mobile numbers
  const getCountryCode = (location: string): string => {
    const countryCodeMap: { [key: string]: string } = {
      'DUBAI': '+971',
      'DOHA': '+974',
      'KUWAIT': '+965',
      'RIYADH': '+966',
      'JEDDAH': '+966',
      'MANAMA': '+973',
      'MUSCAT': '+968',
      'ABU_DHABI': '+971'
    }
    return countryCodeMap[location] || '+971' // Default to UAE
  }

  // Format mobile number with country code
  const formatMobileNumber = (mobile: string, location: string): string => {
    const countryCode = getCountryCode(location)
    return `${countryCode} ${mobile}`
  }

  // Fetch certificates
  const fetchCertificates = useCallback(async () => {
    try {
      setIsLoading(true)
      const token = localStorage.getItem('egl_token')
      
      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sortBy: pagination.sortBy,
        sortOrder: pagination.sortOrder
      })
      
      // Add search parameters
      if (searchParams.query) params.append('query', searchParams.query)
      if (searchParams.status) params.append('status', searchParams.status)
      if (searchParams.validation_status) params.append('validation_status', searchParams.validation_status)
      if (searchParams.date_from) params.append('date_from', searchParams.date_from)
      if (searchParams.date_to) params.append('date_to', searchParams.date_to)
      if (searchParams.created_by) params.append('created_by', searchParams.created_by)
      if (searchParams.gemstone_type) params.append('gemstone_type', searchParams.gemstone_type)
      if (searchParams.metal_composition) params.append('metal_composition', searchParams.metal_composition)
    if (searchParams.gemstone_growth_origin) params.append('gemstone_growth_origin', searchParams.gemstone_growth_origin) // NEW v12.6
      
      const response = await fetch(`/api/certificates?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      const data = await response.json()
      if (data.success) {
        setCertificates(data.data.certificates || [])
        setPagination(prev => ({
          ...prev,
          total: data.data.total || 0,
          totalPages: Math.ceil((data.data.total || 0) / prev.limit)
        }))
      }
    } catch (error) {
      console.error('Error fetching certificates:', error)
    } finally {
      setIsLoading(false)
    }
  }, [pagination.page, pagination.limit, pagination.sortBy, pagination.sortOrder, searchParams])

  // Fetch pending users (admin only)
  const fetchPendingUsers = useCallback(async () => {
    if (user?.role !== 'ADMIN') return

    try {
      const token = localStorage.getItem('egl_token')
      const response = await fetch('/api/auth/admin/users/pending', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      const data = await response.json()
      if (data.success) {
        setPendingUsers(data.data?.users || [])
      }
    } catch (error) {
      console.error('Error fetching pending users:', error)
    }
  }, [user?.role])

  // Fetch admin stats
  const fetchAdminStats = useCallback(async () => {
    if (user?.role !== 'ADMIN') return

    try {
      const token = localStorage.getItem('egl_token')
      const response = await fetch('/api/admin/system/health', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      const data = await response.json()
      if (data.success) {
        setAdminStats(data.data)
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error)
    }
  }, [user?.role])

  // Fetch all users for enable/disable management
  const fetchAllUsers = useCallback(async (showFeedback = false) => {
    try {
      if (showFeedback) {
        setIsRefreshingUsers(true)
        setUsersRefreshMessage('')
      }
      
      const token = localStorage.getItem('egl_token')
      const response = await fetch('/api/auth/admin/users/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      const data = await response.json()
      if (data.success) {
        setAllUsers(data.data.users)
        if (showFeedback) {
          setUsersRefreshMessage(`✅ Users refreshed successfully! (${data.data.users.length} users)`)
          setTimeout(() => setUsersRefreshMessage(''), 3000)
        }
      }
    } catch (error) {
      console.error('Error fetching all users:', error)
      if (showFeedback) {
        setUsersRefreshMessage('❌ Failed to refresh users')
        setTimeout(() => setUsersRefreshMessage(''), 3000)
      }
    } finally {
      if (showFeedback) {
        setIsRefreshingUsers(false)
      }
    }
  }, [])

  // Fetch system settings
  const fetchSystemSettings = useCallback(async (showFeedback = false) => {
    try {
      if (showFeedback) {
        setIsRefreshingSettings(true)
        setSettingsRefreshMessage('')
      }
      
      const token = localStorage.getItem('egl_token')
      const response = await fetch('/api/admin/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      const data = await response.json()
      if (data.success) {
        setSystemSettings(data.data.settings)
        // Find auto-approval setting
        const autoApprovalSetting = data.data.settings.find(
          (s: any) => s.setting_key === 'certificate_auto_approval'
        )
        if (autoApprovalSetting) {
          setAutoApprovalEnabled(autoApprovalSetting.setting_value === 'true')
        }
        
        // Find approval workflow setting
        const approvalWorkflowSetting = data.data.settings.find(
          (s: any) => s.setting_key === 'certificate_approval_required'
        )
        if (approvalWorkflowSetting) {
          setApprovalWorkflowEnabled(approvalWorkflowSetting.setting_value === 'true')
        }
        
        // Find verification settings
        const emailVerificationSetting = data.data.settings.find(
          (s: any) => s.setting_key === 'email_verification_enabled'
        )
        if (emailVerificationSetting) {
          setEmailVerificationEnabled(emailVerificationSetting.setting_value === 'true')
        }
        
        const mobileVerificationSetting = data.data.settings.find(
          (s: any) => s.setting_key === 'mobile_verification_enabled'
        )
        if (mobileVerificationSetting) {
          setMobileVerificationEnabled(mobileVerificationSetting.setting_value === 'true')
        }

        const verificationRequiredForLoginSetting = data.data.settings.find(
          (s: any) => s.setting_key === 'verification_required_for_login'
        )
        if (verificationRequiredForLoginSetting) {
          setVerificationRequiredForLogin(verificationRequiredForLoginSetting.setting_value === 'true')
        }
        
        if (showFeedback) {
          setSettingsRefreshMessage('✅ Settings refreshed successfully!')
          setTimeout(() => setSettingsRefreshMessage(''), 3000)
        }
      }
    } catch (error) {
      console.error('Error fetching system settings:', error)
      if (showFeedback) {
        setSettingsRefreshMessage('❌ Failed to refresh settings')
        setTimeout(() => setSettingsRefreshMessage(''), 3000)
      }
    } finally {
      if (showFeedback) {
        setIsRefreshingSettings(false)
      }
    }
  }, [])

  // Initial data load
  useEffect(() => {
    if (activeModule === 'admin' && user?.role === 'ADMIN') {
      fetchPendingUsers()
      fetchAdminStats()
      fetchAllUsers()
      fetchSystemSettings()
    } else if (activeModule === null && user?.role === 'ADMIN') {
      // Load admin stats for welcome screen
      fetchAdminStats()
    }
  }, [fetchPendingUsers, fetchAdminStats, fetchAllUsers, fetchSystemSettings, activeModule, user?.role])

  // Calculate user statistics
  const calculateUserStats = useCallback(() => {
    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    
    const stats = {
      totalUsers: allUsers.length,
      activeUsers: allUsers.filter(user => user.is_active).length,
      pendingUsers: allUsers.filter(user => user.status === 'PENDING').length,
      rejectedUsers: allUsers.filter(user => user.status === 'REJECTED').length,
      adminUsers: allUsers.filter(user => user.role === 'ADMIN').length,
      creatorUsers: allUsers.filter(user => user.rights === 'CREATOR').length,
      readOnlyUsers: allUsers.filter(user => user.rights === 'READ_ONLY').length,
      dubaiUsers: allUsers.filter(user => user.location === 'DUBAI').length,
      dohaUsers: allUsers.filter(user => user.location === 'DOHA').length,
      recentRegistrations: allUsers.filter(user => {
        const createdAt = new Date(user.created_at)
        return createdAt >= sevenDaysAgo
      }).length
    }
    
    setUserStats(stats)
  }, [allUsers])

  // Calculate user statistics when allUsers changes
  useEffect(() => {
    calculateUserStats()
  }, [calculateUserStats])

  // Handle search
  const handleSearch = (newSearchParams: Partial<SearchParams>) => {
    setSearchParams(prev => ({ ...prev, ...newSearchParams }))
    setPagination(prev => ({ ...prev, page: 1 })) // Reset to first page
  }

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  // Handle sorting
  const handleSort = (sortBy: string) => {
    setPagination(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'DESC' ? 'ASC' : 'DESC',
      page: 1
    }))
  }

  // Toggle certificate selection
  const toggleCertificateSelection = (certId: string) => {
    setSelectedCertificates(prev => 
      prev.includes(certId) 
        ? prev.filter(id => id !== certId)
        : [...prev, certId]
    )
  }

  // Toggle row expansion with error handling
  const toggleRowExpansion = (certId: string) => {
    try {
      if (!certId) {
        console.warn('toggleRowExpansion called with invalid certId:', certId)
        return
      }
      
      setExpandedRows(prev => 
        prev.includes(certId) 
          ? prev.filter(id => id !== certId)
          : [...prev, certId]
      )
    } catch (error) {
      console.error('Error toggling row expansion:', error)
    }
  }

  // Select all certificates
  const toggleSelectAll = () => {
    if (selectedCertificates.length === certificates.length) {
      setSelectedCertificates([])
    } else {
      setSelectedCertificates(certificates.map(cert => cert.id))
    }
  }

  // Export selected certificates
  const handleExport = async () => {
    if (selectedCertificates.length === 0) {
      alert('Please select certificates to export')
      return
    }

    try {
      setIsExporting(true)
      const token = localStorage.getItem('egl_token')
      
      const response = await fetch('/api/certificates/export', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          certificate_ids: selectedCertificates,
          format: 'xlsm',
          filename: `certificates_export_${new Date().toISOString().split('T')[0]}.xlsm`
        })
      })

      const data = await response.json()
      if (data.success) {
        // Create download link
        const link = document.createElement('a')
        link.href = data.data.download_url
        link.download = data.data.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clear selection
        setSelectedCertificates([])
      } else {
        alert('Export failed: ' + (data.message || 'Unknown error'))
      }
    } catch (error) {
      console.error('Export error:', error)
      alert('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  // Approve user (admin only) with feedback
  const approveUser = async (userId: string, username?: string) => {
    try {
      const displayName = username || 'User'
      setUserActionMessages(prev => ({
        ...prev,
        [userId]: `🔄 Approving ${displayName}...`
      }))
      
      const token = localStorage.getItem('egl_token')
      const response = await fetch(`/api/auth/admin/users/${userId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setUserActionMessages(prev => ({
          ...prev,
          [userId]: `✅ ${displayName} approved successfully`
        }))
        fetchPendingUsers() // Refresh the list
        fetchAllUsers() // Also refresh all users list
      } else {
        const errorData = await response.json()
        setUserActionMessages(prev => ({
          ...prev,
          [userId]: `❌ Failed to approve ${displayName}: ${errorData.message}`
        }))
      }
    } catch (error) {
      setUserActionMessages(prev => ({
        ...prev,
        [userId]: `❌ Error approving user`
      }))
    }
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setUserActionMessages(prev => {
        const newMessages = { ...prev }
        delete newMessages[userId]
        return newMessages
      })
    }, 3000)
  }

  // Reject user (admin only)
  const rejectUser = async (userId: string) => {
    try {
      const token = localStorage.getItem('egl_token')
      const response = await fetch(`/api/auth/admin/users/${userId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        fetchPendingUsers() // Refresh the list
      }
    } catch (error) {
      console.error('Error rejecting user:', error)
    }
  }

  // Enable user with feedback
  const enableUser = async (userId: string, username?: string) => {
    try {
      const displayName = username || 'User'
      setUserActionMessages(prev => ({
        ...prev,
        [userId]: `🔄 Enabling ${displayName}...`
      }))
      
      const token = localStorage.getItem('egl_token')
      const response = await fetch(`/api/auth/admin/users/${userId}/enable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        setUserActionMessages(prev => ({
          ...prev,
          [userId]: `✅ ${displayName} enabled successfully`
        }))
        fetchAllUsers() // Refresh the list
      } else {
        const errorData = await response.json()
        setUserActionMessages(prev => ({
          ...prev,
          [userId]: `❌ Failed to enable ${displayName}: ${errorData.message}`
        }))
      }
    } catch (error) {
      setUserActionMessages(prev => ({
        ...prev,
        [userId]: `❌ Error enabling user`
      }))
    }
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setUserActionMessages(prev => {
        const newMessages = { ...prev }
        delete newMessages[userId]
        return newMessages
      })
    }, 3000)
  }

  // Disable user with feedback
  const disableUser = async (userId: string, username?: string) => {
    try {
      const displayName = username || 'User'
      setUserActionMessages(prev => ({
        ...prev,
        [userId]: `🔄 Disabling ${displayName}...`
      }))
      
      const token = localStorage.getItem('egl_token')
      const response = await fetch(`/api/auth/admin/users/${userId}/disable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        setUserActionMessages(prev => ({
          ...prev,
          [userId]: `✅ ${displayName} disabled successfully`
        }))
        fetchAllUsers() // Refresh the list
      } else {
        const errorData = await response.json()
        setUserActionMessages(prev => ({
          ...prev,
          [userId]: `❌ Failed to disable ${displayName}: ${errorData.message}`
        }))
      }
    } catch (error) {
      setUserActionMessages(prev => ({
        ...prev,
        [userId]: `❌ Error disabling user`
      }))
    }
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setUserActionMessages(prev => {
        const newMessages = { ...prev }
        delete newMessages[userId]
        return newMessages
      })
    }, 3000)
  }

  // Toggle auto-approval setting
  const toggleAutoApproval = async () => {
    try {
      const token = localStorage.getItem('egl_token')
      const newValue = !autoApprovalEnabled
      
      const response = await fetch('/api/admin/settings/certificate_auto_approval', {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: newValue.toString() })
      })

      if (response.ok) {
        setAutoApprovalEnabled(newValue)
        console.log(`Auto-approval ${newValue ? 'enabled' : 'disabled'}`)
      }
    } catch (error) {
      console.error('Error toggling auto-approval:', error)
    }
  }

  // Toggle approval workflow setting
  const toggleApprovalWorkflow = async () => {
    try {
      const token = localStorage.getItem('egl_token')
      const newValue = !approvalWorkflowEnabled
      
      const response = await fetch('/api/admin/settings/certificate_approval_required', {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: newValue.toString() })
      })

      if (response.ok) {
        setApprovalWorkflowEnabled(newValue)
        console.log(`Approval workflow ${newValue ? 'enabled' : 'disabled'}`)
      }
    } catch (error) {
      console.error('Error toggling approval workflow:', error)
    }
  }

  // Toggle email verification setting
  const toggleEmailVerification = async () => {
    try {
      const token = localStorage.getItem('egl_token')
      const newValue = !emailVerificationEnabled
      
      const response = await fetch('/api/admin/settings/email_verification_enabled', {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: newValue.toString() })
      })

      if (response.ok) {
        setEmailVerificationEnabled(newValue)
        console.log(`Email verification ${newValue ? 'enabled' : 'disabled'}`)
      }
    } catch (error) {
      console.error('Error toggling email verification:', error)
    }
  }

  // Toggle mobile verification setting
  const toggleMobileVerification = async () => {
    try {
      const token = localStorage.getItem('egl_token')
      const newValue = !mobileVerificationEnabled
      
      const response = await fetch('/api/admin/settings/mobile_verification_enabled', {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: newValue.toString() })
      })

      if (response.ok) {
        setMobileVerificationEnabled(newValue)
        console.log(`Mobile verification ${newValue ? 'enabled' : 'disabled'}`)
      }
    } catch (error) {
      console.error('Error toggling mobile verification:', error)
    }
  }

  // Toggle verification required for login setting
  const toggleVerificationRequiredForLogin = async () => {
    try {
      const token = localStorage.getItem('egl_token')
      const newValue = !verificationRequiredForLogin
      
      const response = await fetch('/api/admin/settings/verification_required_for_login', {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: newValue.toString() })
      })

      if (response.ok) {
        setVerificationRequiredForLogin(newValue)
        console.log(`Verification required for login ${newValue ? 'enabled' : 'disabled'}`)
      }
    } catch (error) {
      console.error('Error toggling verification required for login:', error)
    }
  }

  // Handle refresh settings button click
  const handleRefreshSettings = () => {
    fetchSystemSettings(true)
  }

  // Handle refresh users button click
  const handleRefreshUsers = () => {
    fetchAllUsers(true)
  }

  // Toggle collapsible sections
  const toggleSection = (sectionKey: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  // Update user rights with feedback
  const updateUserRights = async (userId: string, newRights: string, username: string) => {
    try {
      const token = localStorage.getItem('egl_token')
      
      // Show loading message
      setUserActionMessages(prev => ({
        ...prev,
        [userId]: `🔄 Updating ${username} rights to ${newRights}...`
      }))
      
      const response = await fetch(`/api/auth/admin/users/${userId}/rights`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rights: newRights })
      })

      if (response.ok) {
        const data = await response.json()
        setUserActionMessages(prev => ({
          ...prev,
          [userId]: `✅ ${username} rights updated to ${newRights}`
        }))
        fetchAllUsers() // Refresh the user list
      } else {
        const errorData = await response.json()
        setUserActionMessages(prev => ({
          ...prev,
          [userId]: `❌ Failed to update ${username} rights: ${errorData.message}`
        }))
      }
    } catch (error) {
      setUserActionMessages(prev => ({
        ...prev,
        [userId]: `❌ Error updating ${username} rights`
      }))
    }
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setUserActionMessages(prev => {
        const newMessages = { ...prev }
        delete newMessages[userId]
        return newMessages
      })
    }, 3000)
  }

  // Manually validate certificate
  const validateCertificate = async (certificateId: string, validationStatus: string) => {
    try {
      const token = localStorage.getItem('egl_token')
      const response = await fetch(`/api/admin/certificates/${certificateId}/validate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ validation_status: validationStatus })
      })

      if (response.ok) {
        fetchCertificates() // Refresh the certificate list
      }
    } catch (error) {
      console.error('Error validating certificate:', error)
    }
  }

  // Format date with null check
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return 'Invalid Date'
    }
  }

  // Format weight with comprehensive type checking
  const formatWeight = (weight: any) => {
    try {
      // Handle null, undefined, or empty values
      if (weight === null || weight === undefined || weight === '') {
        return '0.000'
      }
      
      // Convert to number if it's a string
      const numWeight = typeof weight === 'string' ? parseFloat(weight) : weight
      
      // Check if it's a valid number
      if (isNaN(numWeight) || typeof numWeight !== 'number') {
        return '0.000'
      }
      
      return numWeight.toFixed(3)
    } catch (error) {
      console.error('formatWeight error:', error, 'input:', weight)
      return '0.000'
    }
  }

  // Get public certificate URL
  const getPublicCertificateUrl = (certId: string) => {
    return `${window.location.origin}/certificate/${certId}`
  }

  return (
    <div className="unified-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>EGL Certificate Management</h1>
          <div className="user-info">
            Welcome, {user?.username} ({user?.role})
          </div>
        </div>
        <div className="header-right">
          <button onClick={logout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="dashboard-nav">
        <button 
          className={`nav-btn`}
          onClick={() => navigate('/certificates')}
          title='View all certificates'
        >
          📋 Certificate List
        </button>
        <button 
          className={`nav-btn ${user?.rights === 'READ_ONLY' ? 'disabled' : ''}`}
          onClick={() => user?.rights !== 'READ_ONLY' && navigate('/create-certificate')}
          disabled={user?.rights === 'READ_ONLY'}
          title={user?.rights === 'READ_ONLY' ? 'You need CREATOR rights to create certificates' : 'Create New Certificate'}
        >
          ➕ Create New Certificate
        </button>
        <button 
          className={`nav-btn`}
          onClick={() => navigate('/upload-images')}
          title='Upload photos to staging'
        >
          📷 Upload Images
        </button>
        <button 
          className={`nav-btn ${activeModule === 'card-printing' ? 'active' : ''}`}
          onClick={() => setActiveModule('card-printing')}
          title='Control Windows Server card printing operations'
        >
          📇 Card Printing
        </button>
        {user?.role === 'ADMIN' && (
          <button 
            className={`nav-btn ${activeModule === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveModule('admin')}
          >
            👑 Admin Panel
          </button>
        )}
        

      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        {/* Welcome Screen - Show when no module is selected */}
        {activeModule === null && (
          <div className="welcome-screen">
            <div className="welcome-cards">
              <div className="welcome-card" onClick={() => navigate('/certificates')}>
                <div className="card-icon">📋</div>
                <h3>Certificate List</h3>
                <p>View, search, filter, and export all certificates with column customization and Excel export.</p>
                <div className="card-features">
                  <span>• Location & sort filtering</span>
                  <span>• Column visibility control (28 columns)</span>
                  <span>• Export to Excel (CSV)</span>
                  <span>• Pagination & search</span>
                </div>
              </div>
              
              <div 
                className={`welcome-card ${user?.rights === 'READ_ONLY' ? 'disabled' : ''}`} 
                onClick={() => user?.rights !== 'READ_ONLY' && navigate('/create-certificate')}
                title={user?.rights === 'READ_ONLY' ? 'You need CREATOR rights to create certificates' : 'Create New Certificate'}
              >
                <div className="card-icon">➕</div>
                <h3>Create New Certificate</h3>
                <p>Create new certificates with real-time validation, S3 image upload, and field locking.</p>
                {user?.rights === 'READ_ONLY' && (
                  <div className="rights-restriction">
                    <span>🔒 Requires CREATOR rights</span>
                  </div>
                )}
                <div className="card-features">
                  <span>• Guided form interface</span>
                  <span>• Real-time validation</span>
                  <span>• Image upload support</span>
                  <span>• Draft saving</span>
                </div>
              </div>
              
              <div className="welcome-card" onClick={() => navigate('/upload-images')}>
                <div className="card-icon">📷</div>
                <h3>Upload Images</h3>
                <p>Upload photos from your phone with 1:1 square cropping to location-based staging folders.</p>
                <div className="card-features">
                  <span>• Mobile camera support</span>
                  <span>• 1:1 square crop with zoom</span>
                  <span>• Auto JPG conversion</span>
                  <span>• Location-based staging</span>
                </div>
              </div>
              
              <div className="welcome-card" onClick={() => setActiveModule('card-printing')}>
                <div className="card-icon">📇</div>
                <h3>Card Printing</h3>
                <p>Control Windows Server card printing operations with live status monitoring and remote control.</p>
                <div className="card-features">
                  <span>• Live desktop screenshots</span>
                  <span>• S3 sync control</span>
                  <span>• Print Studio automation</span>
                  <span>• Workflow monitoring</span>
                </div>
              </div>
              
              {user?.role === 'ADMIN' && (
                <div className="welcome-card" onClick={() => setActiveModule('admin')}>
                  <div className="card-icon">👑</div>
                  <h3>Admin Panel</h3>
                  <p>Complete system administration with user management, S3 cleanup, and system settings.</p>
                  <div className="card-features">
                    <span>• User approval & management</span>
                    <span>• System settings control</span>
                    <span>• S3 orphan cleanup</span>
                    <span>• Health monitoring</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="system-status">
              <h4>📊 Quick System Status</h4>
              {adminStats && (
                <div className="status-grid">
                  <div className="status-item">
                    <span className="status-label">Health Score:</span>
                    <span className={`status-value ${adminStats.healthScore > 80 ? 'healthy' : adminStats.healthScore > 60 ? 'warning' : 'error'}`}>
                      {adminStats.healthScore}%
                    </span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Active Routes:</span>
                    <span className="status-value">{adminStats.metrics?.activeRoutes || 0}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Database Size:</span>
                    <span className="status-value">{adminStats.metrics?.databaseSize || 'N/A'}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Total Users:</span>
                    <span className="status-value">{userStats.totalUsers}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Active Users:</span>
                    <span className="status-value">{userStats.activeUsers}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Pending Users:</span>
                    <span className="status-value">{userStats.pendingUsers}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Card Printing Module - Available to ALL users */}
        {activeModule === 'card-printing' && (
          <CardPrintingSection />
        )}

        {activeModule === 'admin' && user?.role === 'ADMIN' && (
          <div className="admin-module">
            <h2>👑 Admin Control Panel</h2>
            
            {/* System Stats */}
            {adminStats && (
              <div className="admin-stats">
                <h3>📊 System Health</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-value">{adminStats.healthScore}%</div>
                    <div className="stat-label">Health Score</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{adminStats.metrics?.totalRoutes || 0}</div>
                    <div className="stat-label">Total Routes</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{adminStats.metrics?.activeRoutes || 0}</div>
                    <div className="stat-label">Active Routes</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{adminStats.metrics?.databaseSize || 'N/A'}</div>
                    <div className="stat-label">Database Size</div>
                  </div>
                </div>
              </div>
            )}

            {/* System Settings */}
            <div className="system-settings">
              <h3>⚙️ System Settings</h3>
              <button 
                className="refresh-btn"
                onClick={handleRefreshSettings}
                disabled={isRefreshingSettings}
                style={{ marginBottom: '1rem' }}
              >
                {isRefreshingSettings ? '🔄 Refreshing...' : '🔄 Refresh Settings'}
              </button>
              
              {settingsRefreshMessage && (
                <div className="refresh-message" style={{ marginBottom: '1rem', padding: '0.5rem', borderRadius: '4px', backgroundColor: settingsRefreshMessage.includes('✅') ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)', color: settingsRefreshMessage.includes('✅') ? '#28a745' : '#dc3545' }}>
                  {settingsRefreshMessage}
                </div>
              )}
              
              {/* Certificate Auto-Approval Toggle */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4>Certificate Auto-Approval</h4>
                  <p>Automatically approve certificates when validation status is set to VALID</p>
                </div>
                <div className="setting-control">
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={autoApprovalEnabled}
                      onChange={toggleAutoApproval}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`setting-status ${autoApprovalEnabled ? 'enabled' : 'disabled'}`}>
                    {autoApprovalEnabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
              </div>
              
              {autoApprovalEnabled && (
                <div className="auto-approval-info">
                  <p>🤖 <strong>Auto-Approval Active:</strong> Certificates with validation_status = 'VALID' will automatically be published.</p>
                </div>
              )}

              {/* Certificate Approval Workflow Toggle */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4>Certificate Approval Workflow</h4>
                  <p>Require manual approval for all certificates before they can be published</p>
                </div>
                <div className="setting-control">
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={approvalWorkflowEnabled}
                      onChange={toggleApprovalWorkflow}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`setting-status ${approvalWorkflowEnabled ? 'enabled' : 'disabled'}`}>
                    {approvalWorkflowEnabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
              </div>
              
              {approvalWorkflowEnabled && (
                <div className="auto-approval-info">
                  <p>📋 <strong>Approval Workflow Active:</strong> All certificates will require manual admin approval before publication.</p>
                </div>
              )}

              {/* Email Verification Toggle */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4>Email Verification</h4>
                  <p>Require users to verify their email addresses before account activation</p>
                </div>
                <div className="setting-control">
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={emailVerificationEnabled}
                      onChange={toggleEmailVerification}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`setting-status ${emailVerificationEnabled ? 'enabled' : 'disabled'}`}>
                    {emailVerificationEnabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
              </div>

              {/* Mobile Verification Toggle */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4>Mobile Verification</h4>
                  <p>Require users to verify their mobile numbers before account activation</p>
                </div>
                <div className="setting-control">
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={mobileVerificationEnabled}
                      onChange={toggleMobileVerification}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`setting-status ${mobileVerificationEnabled ? 'enabled' : 'disabled'}`}>
                    {mobileVerificationEnabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
              </div>

              {/* Verification Required for Login Toggle */}
              <div className="setting-item">
                <div className="setting-info">
                  <h4>Verification Required for Login</h4>
                  <p>Block login access for users who haven't completed verification</p>
                </div>
                <div className="setting-control">
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={verificationRequiredForLogin}
                      onChange={toggleVerificationRequiredForLogin}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`setting-status ${verificationRequiredForLogin ? 'enabled' : 'disabled'}`}>
                    {verificationRequiredForLogin ? 'ENABLED' : 'DISABLED'}
                  </span>
                </div>
              </div>

              {(emailVerificationEnabled || mobileVerificationEnabled || verificationRequiredForLogin) && (
                <div className="verification-info">
                  <p>📧 <strong>Verification System Active:</strong> Users will need to complete verification steps based on enabled settings.</p>
                </div>
              )}
            </div>

            {/* Pending Users */}
            <div className="pending-users">
              <h3>👥 Pending User Approvals ({pendingUsers.length})</h3>
              {pendingUsers.length === 0 ? (
                <p>No pending user approvals.</p>
              ) : (
                <div className="users-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Location</th>
                        <th>Registered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.firstname} {user.lastname}</td>
                          <td>{user.username}</td>
                          <td>{user.email}</td>
                          <td>{formatMobileNumber(user.mobilenumber, user.location)}</td>
                          <td>{user.location}</td>
                          <td>{formatDate(user.created_at)}</td>
                          <td>
                            <button 
                              className="approve-btn"
                              onClick={() => approveUser(user.id)}
                            >
                              ✅ Approve
                            </button>
                            <button 
                              className="reject-btn"
                              onClick={() => rejectUser(user.id)}
                            >
                              ❌ Reject
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* User Management */}
            <div className="user-management">
              <h3>🔧 User Management ({allUsers.length})</h3>
              
              {/* User Statistics */}
              <div className="user-stats-section">
                <h4>📊 User Statistics</h4>
                <div className="user-stats-grid">
                  <div className="stat-card">
                    <div className="stat-number">{userStats.totalUsers}</div>
                    <div className="stat-label">Total Users</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.activeUsers}</div>
                    <div className="stat-label">Active Users</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.pendingUsers}</div>
                    <div className="stat-label">Pending</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.rejectedUsers}</div>
                    <div className="stat-label">Rejected</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.adminUsers}</div>
                    <div className="stat-label">Admins</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.creatorUsers}</div>
                    <div className="stat-label">Creators</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.readOnlyUsers}</div>
                    <div className="stat-label">Read Only</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.dubaiUsers}</div>
                    <div className="stat-label">Dubai</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.dohaUsers}</div>
                    <div className="stat-label">Doha</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{userStats.recentRegistrations}</div>
                    <div className="stat-label">Last 7 Days</div>
                  </div>
                </div>
              </div>
              
              <button 
                className="refresh-btn"
                onClick={handleRefreshUsers}
                disabled={isRefreshingUsers}
                style={{ marginBottom: '1rem' }}
              >
                {isRefreshingUsers ? '🔄 Refreshing...' : '🔄 Refresh Users'}
              </button>
              
              {usersRefreshMessage && (
                <div className="refresh-message" style={{ marginBottom: '1rem', padding: '0.5rem', borderRadius: '4px', backgroundColor: usersRefreshMessage.includes('✅') ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)', color: usersRefreshMessage.includes('✅') ? '#28a745' : '#dc3545' }}>
                  {usersRefreshMessage}
                </div>
              )}
              {allUsers.length === 0 ? (
                <p>No users found.</p>
              ) : (
                <div className="users-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Role</th>
                        <th>Rights</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.firstname} {user.lastname}</td>
                          <td>{user.username}</td>
                          <td>{user.email}</td>
                          <td>{formatMobileNumber(user.mobilenumber, user.location)}</td>
                          <td>
                            <span className={`role-badge ${user.role?.toLowerCase()}`}>
                              {user.role}
                            </span>
                          </td>
                          <td>
                            {user.role !== 'ADMIN' ? (
                              <div className="rights-controls">
                                <div className="rights-buttons">
                                  <button
                                    className={`rights-btn ${user.rights === 'CREATOR' ? 'active' : ''}`}
                                    onClick={() => user.rights !== 'CREATOR' && updateUserRights(user.id, 'CREATOR', user.username)}
                                    disabled={user.rights === 'CREATOR'}
                                  >
                                    CREATOR
                                  </button>
                                  <button
                                    className={`rights-btn ${user.rights === 'READ_ONLY' ? 'active' : ''}`}
                                    onClick={() => user.rights !== 'READ_ONLY' && updateUserRights(user.id, 'READ_ONLY', user.username)}
                                    disabled={user.rights === 'READ_ONLY'}
                                  >
                                    READ_ONLY
                                  </button>
                                </div>
                                {userActionMessages[user.id] && (
                                  <div className="action-feedback">
                                    {userActionMessages[user.id]}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="rights-badge admin">
                                {user.rights}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${user.status?.toLowerCase()}`}>
                              {user.status}
                            </span>
                          </td>
                          <td>
                            {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
                          </td>
                          <td>
                            {user.role !== 'ADMIN' ? (
                              <div className="user-actions">
                                <div className="action-buttons">
                                  {user.status === 'REJECTED' && (
                                    <button 
                                      className="approve-btn"
                                      onClick={() => approveUser(user.id, user.username)}
                                      title="Re-approve this rejected user"
                                    >
                                      ✅ Re-Approve
                                    </button>
                                  )}
                                  {user.status === 'DISABLED' && (
                                    <button 
                                      className="enable-btn"
                                      onClick={() => enableUser(user.id, user.username)}
                                      title="Enable this disabled user"
                                    >
                                      🔓 Enable
                                    </button>
                                  )}
                                  {(user.status === 'APPROVED' || user.status === 'ENABLED') && (
                                    <button 
                                      className="disable-btn"
                                      onClick={() => disableUser(user.id, user.username)}
                                      title="Disable this user"
                                    >
                                      🔒 Disable
                                    </button>
                                  )}
                                </div>
                                {userActionMessages[user.id] && (
                                  <div className="action-feedback">
                                    {userActionMessages[user.id]}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="admin-protected">🛡️ Protected</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* S3 Cleanup Section - Integrated in Admin Panel */}
            <div className="s3-cleanup-section">
              <OrphanImageCleanup />
            </div>

            {/* Location Permissions Management Section */}
            <div className="location-permissions-section">
              <LocationPermissionsManager />
            </div>

            <div className="force-clear-section">
              <ForceClearPanel />
            </div>

            {/* User Password Management Section - Added 2025-11-18 */}
            <div className="password-management-section">
              <UserPasswordManager />
            </div>
          </div>
        )}

      </div>
    </div>
  )
} 